import React, { useState, useEffect } from 'react';
import { useAdminEdit } from '../contexts/AdminEditContext';
import { Edit2, Check, X } from 'lucide-react';

const EditableText = ({ 
  value, 
  onSave, 
  className = '', 
  tag: Tag = 'span',
  placeholder = 'Enter text...',
  multiline = false
}) => {
  const { isEditMode, isAdmin } = useAdminEdit();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  // Update editValue when value prop changes (e.g., after restore)
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  if (!isAdmin) {
    return <Tag className={className}>{value}</Tag>;
  }

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (!isEditMode) {
    return <Tag className={className}>{value}</Tag>;
  }

  // When editing, maintain the same visual structure
  const editingWrapperClass = multiline ? 'block' : 'inline-block';

  if (isEditing) {
    // Extract non-color classes from className for the input
    const baseClasses = className.split(' ').filter(cls => 
      !cls.includes('text-') && !cls.includes('text-white') && !cls.includes('text-gray')
    ).join(' ');
    
    return (
      <div className={`relative ${editingWrapperClass}`} style={{ minWidth: multiline ? '100%' : '200px' }}>
        {multiline ? (
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`${baseClasses} border-2 border-primary-500 rounded px-2 py-1 min-h-[60px] bg-white dark:bg-gray-800 w-full`}
            placeholder={placeholder}
            autoFocus
            style={{ color: '#111827' }}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className={`${baseClasses} border-2 border-primary-500 rounded px-2 py-1 bg-white dark:bg-gray-800`}
            placeholder={placeholder}
            autoFocus
            style={{ color: '#111827', width: '100%' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !multiline) {
                handleSave();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
          />
        )}
        <div className="absolute -right-12 top-0 flex gap-1 z-20">
          <button
            onClick={handleSave}
            className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
            title="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={handleCancel}
            className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group inline-block w-full">
      <Tag className={className}>{value}</Tag>
      <button
        onClick={() => setIsEditing(true)}
        className="absolute -top-1 -right-1 p-1 bg-primary-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
        title="Edit text"
      >
        <Edit2 className="w-3 h-3" />
      </button>
    </div>
  );
};

export default EditableText;
